import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Link2, Building2, User, Mail, Phone, FileText, Download, Printer, Upload, Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ExcelImportDialog from '@/components/ExcelImportDialog';
import { toast } from 'sonner';
import axios from 'axios';
import { TenureFields } from './TenureFields';
import { ContactGroups } from './ContactGroups';
import { tenureForm, tenurePayload, tenureStatus, STATUS_LABELS, displayDate, TENURE_REASONS } from './tenure';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Relationship types with Italian labels
const RELATIONSHIP_TYPES = [
  { value: 'OWNER', label: 'Proprietario', color: 'bg-blue-100 text-blue-800' },
  { value: 'CO_OWNER', label: 'Comproprietario', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'BARE_OWNER', label: 'Nudo proprietario', color: 'bg-purple-100 text-purple-800' },
  { value: 'USUFRUCTUARY', label: 'Usufruttuario', color: 'bg-amber-100 text-amber-800' },
  { value: 'TENANT', label: 'Affittuario', color: 'bg-green-100 text-green-800' },
  { value: 'EX_OWNER', label: 'Ex proprietario', color: 'bg-stone-200 text-stone-700' },
];

const getRelationshipLabel = (type) => {
  const rel = RELATIONSHIP_TYPES.find(r => r.value === type);
  return rel ? rel.label : type;
};

const getRelationshipColor = (type) => {
  const rel = RELATIONSHIP_TYPES.find(r => r.value === type);
  return rel ? rel.color : 'bg-gray-100 text-gray-800';
};

// Tipi di documento allegabili a una persona (es. scansione documento d'identità)
const PERSON_DOC_TYPES = [
  { value: 'DOCUMENTO_IDENTITA', label: "Documento d'identità" },
  { value: 'CODICE_FISCALE', label: 'Codice fiscale / Tessera sanitaria' },
  { value: 'CONTRATTO', label: 'Contratto / Atto' },
  { value: 'ALTRO', label: 'Altro' },
];

export const PeopleTab = ({ condominiumId }) => {
  const [people, setPeople] = useState([]);
  const [units, setUnits] = useState([]);
  // tutte le titolarita' del condominio, per mostrare il ruolo gia' nell'elenco
  const [allRelationships, setAllRelationships] = useState([]);
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [tenureFilter, setTenureFilter] = useState('ALL');
  const [groupMode, setGroupMode] = useState('NONE');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personRelationships, setPersonRelationships] = useState([]);
  const [activeDialogTab, setActiveDialogTab] = useState('info');
  const [isImportOpen, setIsImportOpen] = useState(false);
  // Documenti della persona (allegati)
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [docsPerson, setDocsPerson] = useState(null);
  const [personDocs, setPersonDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState('DOCUMENTO_IDENTITA');

  const [formData, setFormData] = useState({
    full_name: '',
    fiscal_code: '',
    email: '',
    pec: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    postal_code: '',
    notes: '',
    preferred_channel: '',
  });

  const [linkFormData, setLinkFormData] = useState({
    unit_id: '',
    relationship_type: 'OWNER',
    ownership_percentage: '',
    notes: '',
    to_verify: false,
    ...tenureForm(),
  });
  // titolarita' in corso di modifica (null = si sta creando un nuovo collegamento)
  const [editingRelationship, setEditingRelationship] = useState(null);

  useEffect(() => {
    fetchPeople();
    fetchUnits();
    fetchAllRelationships();
  }, [condominiumId]);

  const fetchPeople = async () => {
    try {
      const response = await axios.get(`${API}/condominiums/${condominiumId}/people`);
      setPeople(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle persone');
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await axios.get(`${API}/condominiums/${condominiumId}/units`);
      setUnits(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  // una sola chiamata per tutte le titolarita': serve a mostrare il ruolo
  // (proprietario, nudo proprietario, usufruttuario, affittuario...) gia'
  // nell'elenco, senza aprire la scheda di ogni soggetto
  const fetchAllRelationships = async () => {
    try {
      const response = await axios.get(`${API}/condominiums/${condominiumId}/relationships`);
      setAllRelationships(response.data || []);
    } catch (error) {
      console.error('Error fetching relationships:', error);
      setAllRelationships([]);
    }
  };

  // ruoli di un soggetto, con l'unita' a cui si riferiscono
  const ruoliDi = (personId) =>
    allRelationships.filter((r) => r.person_id === personId);

  const personeVisibili = people.filter(p => {
    if (roleFilter === 'ALL' && tenureFilter === 'ALL') return true;
    return ruoliDi(p.id).some(r =>
      (roleFilter === 'ALL' || r.relationship_type === roleFilter) &&
      (tenureFilter === 'ALL' || tenureStatus(r) === tenureFilter));
  });

  const conteggioRuolo = (tipo) =>
    people.filter((p) => ruoliDi(p.id).some((r) => r.relationship_type === tipo)).length;

  const senzaTitolarita = people.filter((p) => ruoliDi(p.id).length === 0).length;

  const fetchPersonRelationships = async (personId) => {
    try {
      const response = await axios.get(`${API}/condominiums/${condominiumId}/people/${personId}/relationships`);
      setPersonRelationships(response.data || []);
    } catch (error) {
      console.error('Error fetching relationships:', error);
      setPersonRelationships([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = Object.entries(formData).reduce((acc, [key, value]) => {
        // in modifica un campo svuotato va mandato come null: se lo si omette il server lo lascia com'e' e il contatto cancellato ricompare
        if (value !== '') acc[key] = value;
        else if (editingPerson) acc[key] = null;
        return acc;
      }, {});

      if (editingPerson) {
        await axios.put(`${API}/condominiums/${condominiumId}/people/${editingPerson.id}`, data);
        toast.success('Soggetto aggiornato');
      } else {
        await axios.post(`${API}/condominiums/${condominiumId}/people`, data);
        toast.success('Soggetto creato');
      }

      setDialogOpen(false);
      resetForm();
      await fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkToUnit = async (e) => {
    e.preventDefault();
    if (!selectedPerson || !linkFormData.unit_id) {
      toast.error('Seleziona un\'unità');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...tenurePayload(linkFormData),
        person_id: selectedPerson.id,
        relationship_type: linkFormData.relationship_type,
        ownership_percentage: linkFormData.ownership_percentage ? parseFloat(linkFormData.ownership_percentage) : null,
        notes: linkFormData.notes || null,
        to_verify: !!linkFormData.to_verify,
      };

      if (editingRelationship) {
        await axios.put(
          `${API}/condominiums/${condominiumId}/units/${editingRelationship.unit_id}/relationships/${editingRelationship.id}`,
          data
        );
        toast.success('Titolarità aggiornata');
      } else {
        await axios.post(
          `${API}/condominiums/${condominiumId}/units/${linkFormData.unit_id}/relationships`,
          data
        );
        toast.success('Collegamento creato');
      }
      setLinkDialogOpen(false);
      resetLinkForm();
      await fetchPersonRelationships(selectedPerson.id);
      await fetchAllRelationships();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message || 'Errore nel collegamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRelationship = async (relationshipId) => {
    if (!confirm('Sei sicuro di voler rimuovere questo collegamento?')) return;
    
    try {
      // Find the relationship to get unit_id
      const rel = personRelationships.find(r => r.id === relationshipId);
      if (rel) {
        const res = await axios.delete(
          `${API}/condominiums/${condominiumId}/units/${rel.unit_id}/relationships/${relationshipId}`
        );
        // se alla posizione sono agganciati saldi di apertura la titolarita' viene
        // chiusa con una data di fine invece che cancellata, per non perdere la storia
        if (res.data?.closed) {
          toast.info(res.data.message || 'Titolarità chiusa e conservata nello storico');
        } else {
          toast.success('Collegamento rimosso');
        }
        await fetchPersonRelationships(selectedPerson.id);
        await fetchAllRelationships();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const handleDelete = async (personId) => {
    if (!confirm('Sei sicuro di voler eliminare questo soggetto?')) return;

    try {
      await axios.delete(`${API}/condominiums/${condominiumId}/people/${personId}`);
      toast.success('Soggetto eliminato');
      await fetchPeople();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  const resetForm = () => {
    setEditingPerson(null);
    setActiveDialogTab('info');
    setFormData({
      full_name: '',
      fiscal_code: '',
      email: '',
      pec: '',
      phone: '',
      mobile: '',
      address: '',
      city: '',
      postal_code: '',
      notes: '',
    });
  };

  const resetLinkForm = () => {
    setEditingRelationship(null);
    setLinkFormData({
      unit_id: '',
      relationship_type: 'OWNER',
      ownership_percentage: '',
      notes: '',
      to_verify: false,
      ...tenureForm(),
    });
  };

  // Apre il dialogo su una titolarita' gia' registrata, per correggerla.
  const openEditRelationship = (rel) => {
    setEditingRelationship(rel);
    setLinkFormData({
      unit_id: rel.unit_id,
      relationship_type: rel.relationship_type,
      ownership_percentage: rel.ownership_percentage ?? '',
      notes: rel.notes || '',
      to_verify: !!rel.to_verify,
      ...tenureForm(rel),
    });
    setLinkDialogOpen(true);
  };

  const openEditDialog = async (person) => {
    setEditingPerson(person);
    setSelectedPerson(person);
    setFormData({
      full_name: person.full_name || '',
      fiscal_code: person.fiscal_code || '',
      email: person.email || '',
      pec: person.pec || '',
      phone: person.phone || '',
      mobile: person.mobile || '',
      address: person.address || '',
      city: person.city || '',
      postal_code: person.postal_code || '',
      notes: person.notes || '',
      preferred_channel: person.preferred_channel || '',
    });
    await fetchPersonRelationships(person.id);
    setDialogOpen(true);
  };

  const openLinkDialog = (person) => {
    setSelectedPerson(person);
    resetLinkForm();
    setLinkDialogOpen(true);
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    if (!unit) return unitId;
    const dettagli = [unit.building, unit.floor != null ? `piano ${unit.floor}` : null]
      .filter(Boolean)
      .join(' · ');
    return dettagli ? `${unit.unit_number} — ${dettagli}` : unit.unit_number;
  };

  const handleDownloadPdf = async (person, openInBrowser = false) => {
    try {
      const endpoint = openInBrowser 
        ? `${API}/registry/people/${person.id}/print-preview`
        : `${API}/registry/people/${person.id}/pdf`;
      
      const response = await axios.get(endpoint, {
        params: { condominium_id: condominiumId },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      if (openInBrowser) {
        // Open in new tab for print preview
        const printWindow = window.open(url, '_blank');
        // Trigger print dialog after PDF loads
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        // Download file - browser will ask where to save
        const link = document.createElement('a');
        link.href = url;
        const fileName = `anagrafica_${person.full_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('PDF scaricato con successo');
      }
      
      // Cleanup
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Errore nella generazione del PDF');
    }
  };

  // ===== Documenti della persona (allegati: ID, CF, contratti...) =====
  const fetchPersonDocs = async (personId) => {
    setDocsLoading(true);
    try {
      const res = await axios.get(`${API}/condominiums/${condominiumId}/people/${personId}/documents`);
      setPersonDocs(res.data || []);
    } catch (error) {
      setPersonDocs([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const openDocsDialog = async (person) => {
    setDocsPerson(person);
    setDocType('DOCUMENTO_IDENTITA');
    setDocsDialogOpen(true);
    await fetchPersonDocs(person.id);
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docsPerson) return;
    const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xls', '.xlsx'];
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error('Formato non supportato. Ammessi: PDF, JPG, PNG, Word, Excel');
      e.target.value = '';
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      await axios.post(
        `${API}/condominiums/${condominiumId}/people/${docsPerson.id}/documents`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Documento caricato');
      await fetchPersonDocs(docsPerson.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/condominiums/${condominiumId}/people/${docsPerson.id}/documents/${doc.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.file_type || 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name || 'documento';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${API}/condominiums/${condominiumId}/people/${docsPerson.id}/documents/${docId}`);
      toast.success('Documento eliminato');
      await fetchPersonDocs(docsPerson.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Soggetti</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Gestione anagrafica proprietari, affittuari e altri soggetti
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setIsImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Importa Excel
          </Button>
          <Button size="sm" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-person-button">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Soggetto
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* filtri per ruolo: si vede a colpo d'occhio quanti proprietari,
            nudi proprietari, usufruttuari e affittuari ci sono */}
        {people.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => setRoleFilter('ALL')}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                roleFilter === 'ALL'
                  ? 'bg-stone-800 text-white border-stone-800'
                  : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
              }`}
            >
              Tutti ({people.length})
            </button>
            {RELATIONSHIP_TYPES.map((t) => {
              const n = conteggioRuolo(t.value);
              return (
                <button
                  key={t.value}
                  type="button"
                  disabled={n === 0}
                  onClick={() => setRoleFilter(t.value)}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    roleFilter === t.value
                      ? 'bg-stone-800 text-white border-stone-800'
                      : n === 0
                        ? 'bg-stone-50 text-stone-400 border-stone-200 cursor-not-allowed'
                        : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
                  }`}
                >
                  {t.label} ({n})
                </button>
              );
            })}
            {senzaTitolarita > 0 && (
              <span className="text-xs text-stone-500 ml-1">
                {senzaTitolarita} senza titolarità
              </span>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div><Label htmlFor="tenure-filter">Titolarità</Label>
            <select id="tenure-filter" className="w-full border rounded-md p-2 text-sm" value={tenureFilter} onChange={e => setTenureFilter(e.target.value)}>
              <option value="ALL">Tutte, compreso lo storico</option>
              {Object.entries(STATUS_LABELS).map(([key,label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
          <div><Label htmlFor="contact-group">Raggruppa per recapito</Label>
            <select id="contact-group" className="w-full border rounded-md p-2 text-sm" value={groupMode} onChange={e => setGroupMode(e.target.value)}>
              <option value="NONE">Nessun raggruppamento</option>
              <option value="PEC">Stessa PEC</option>
              <option value="POSTAL">Stesso indirizzo raccomandata</option>
            </select>
          </div>
        </div>
        {groupMode !== 'NONE' && <ContactGroups people={personeVisibili} mode={groupMode} onSelect={openEditDialog} />}
        {people.length > 0 && personeVisibili.length === 0 && <p className="text-sm text-muted-foreground py-3">Nessun soggetto corrisponde ai filtri selezionati.</p>}
        {people.length === 0 ? (
          <div className="text-center py-12 text-stone-600">
            <User className="h-12 w-12 mx-auto mb-4 text-stone-400" />
            <p className="font-medium">Nessun soggetto registrato</p>
            <p className="text-sm mt-1">Aggiungi proprietari, affittuari o altri soggetti</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Codice Fiscale</TableHead>
                <TableHead>Contatti</TableHead>
                <TableHead>PEC</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {personeVisibili.map((person) => (
                <TableRow key={person.id}>
                  <TableCell className="font-medium">{person.full_name}</TableCell>
                  <TableCell>
                    {ruoliDi(person.id).length === 0 ? (
                      <span className="text-xs text-stone-400 italic">nessuna titolarità</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {ruoliDi(person.id).map((rel) => (
                          <Badge
                            key={rel.id}
                            className={`${getRelationshipColor(rel.relationship_type)} text-xs font-normal`}
                            title={`${getRelationshipLabel(rel.relationship_type)} — unità ${rel.unit_number || 'n/d'}`}
                          >
                            {getRelationshipLabel(rel.relationship_type)}
                            {rel.unit_number ? ` · ${rel.unit_number}` : ''}
                            {` · ${STATUS_LABELS[tenureStatus(rel)]}`}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{person.fiscal_code || '-'}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {person.email && (
                        <div className="flex items-center gap-1 text-sm">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          <span>{person.email}</span>
                        </div>
                      )}
                      {(person.mobile || person.phone) && (
                        <div className="flex items-center gap-1 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span>{person.mobile || person.phone}</span>
                        </div>
                      )}
                      {!person.email && !person.mobile && !person.phone && '-'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{person.pec || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {/* PDF/Print Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" title="Genera documento">
                            <FileText className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDownloadPdf(person, false)}>
                            <Download className="h-4 w-4 mr-2" />
                            Scarica PDF Anagrafica
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPdf(person, true)}>
                            <Printer className="h-4 w-4 mr-2" />
                            Stampa Anagrafica
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openLinkDialog(person)}
                        title="Collega a unità"
                      >
                        <Link2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDocsDialog(person)}
                        title="Documenti (identità, contratti...)"
                        data-testid="person-docs-button"
                      >
                        <Paperclip className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDialog(person)}
                        title="Modifica"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(person.id)} 
                        className="text-red-700"
                        title="Elimina"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Edit/Create Person Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPerson ? 'Modifica Soggetto' : 'Nuovo Soggetto'}</DialogTitle>
            <DialogDescription>
              Inserisci i dati anagrafici e i contatti del soggetto
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeDialogTab} onValueChange={setActiveDialogTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Dati Anagrafici</TabsTrigger>
              <TabsTrigger value="units" disabled={!editingPerson}>
                Unità Collegate ({personRelationships.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="info" className="mt-4">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Informazioni Personali
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="full_name">Nome Completo *</Label>
                      <Input 
                        id="full_name" 
                        value={formData.full_name} 
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} 
                        placeholder="es. Mario Rossi"
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="fiscal_code">Codice Fiscale</Label>
                      <Input 
                        id="fiscal_code" 
                        value={formData.fiscal_code} 
                        onChange={(e) => setFormData({ ...formData, fiscal_code: e.target.value.toUpperCase() })} 
                        maxLength={16}
                        placeholder="RSSMRA80A01H501U"
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Contatti
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        value={formData.email} 
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="mario.rossi@email.it"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pec">PEC (opzionale)</Label>
                      <Input
                        id="pec"
                        type="email"
                        value={formData.pec}
                        onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
                        placeholder="mario.rossi@pec.it"
                      />
                    </div>
                    <div>
                      <Label>Come riceve le comunicazioni ufficiali</Label>
                      <Select
                        value={formData.preferred_channel || 'AUTO'}
                        onValueChange={(v) => setFormData({ ...formData, preferred_channel: v === 'AUTO' ? '' : v })}
                      >
                        <SelectTrigger data-testid="canale-preferito"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AUTO">Automatico (PEC, poi email, poi stampa)</SelectItem>
                          <SelectItem value="PEC">PEC</SelectItem>
                          <SelectItem value="EMAIL">Email ordinaria</SelectItem>
                          <SelectItem value="RACCOMANDATA">Raccomandata A/R (stampa)</SelectItem>
                          <SelectItem value="MANO">Consegna a mano in portineria (stampa)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefono Fisso</Label>
                      <Input 
                        id="phone" 
                        value={formData.phone} 
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="091 1234567"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Cellulare</Label>
                      <Input 
                        id="mobile" 
                        value={formData.mobile} 
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Indirizzo
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="address">Indirizzo</Label>
                      <Input 
                        id="address" 
                        value={formData.address} 
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Via Roma, 1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">Città</Label>
                      <Input 
                        id="city" 
                        value={formData.city} 
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="Palermo"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">CAP</Label>
                      <Input 
                        id="postal_code" 
                        value={formData.postal_code} 
                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                        maxLength={5}
                        placeholder="90100"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Note</Label>
                  <Textarea 
                    id="notes" 
                    value={formData.notes} 
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Note aggiuntive..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Salvataggio...' : 'Salva'}
                  </Button>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="units" className="mt-4">
              <div className="space-y-4">
                {/* Add Link Button */}
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    Unità immobiliari collegate a questo soggetto
                  </p>
                  <Button size="sm" onClick={() => openLinkDialog(editingPerson)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Collega Unità
                  </Button>
                </div>
                
                {/* Relationships List */}
                {personRelationships.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg">
                    <Building2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                    <p>Nessuna unità collegata</p>
                    <p className="text-sm">Collega questo soggetto a una o più unità immobiliari</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {personRelationships.map((rel) => (
                      <div key={rel.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{getUnitName(rel.unit_id)}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={getRelationshipColor(rel.relationship_type)}>
                                {getRelationshipLabel(rel.relationship_type)}
                              </Badge>
                              {rel.ownership_percentage && (
                                <span className="text-sm text-muted-foreground">
                                  Quota: {rel.ownership_percentage}%
                                </span>
                              )}
                              {rel.to_verify && (
                                <Badge className="bg-amber-100 text-amber-900">
                                  Da verificare
                                </Badge>
                              )}
                              {rel.valid_from && (
                                <span className="text-sm text-muted-foreground">
                                  dal {displayDate(rel.valid_from)}
                                </span>
                              )}
                              {rel.valid_to && (
                                <span className="text-sm text-muted-foreground">
                                  al {displayDate(rel.valid_to)} (incluso)
                                </span>
                              )}
                            </div>
                            <p className="text-xs mt-1">{STATUS_LABELS[tenureStatus(rel)]}{!rel.valid_from ? ' · data iniziale non indicata' : ''}</p>
                            {[['acquisition_reason','Acquisizione'],['cessation_reason','Cessazione']].map(([key,label]) => rel[key] &&
                              <p key={key} className="text-xs text-muted-foreground">{label}: {TENURE_REASONS.find(([code]) => code === rel[key])?.[1] || rel[key]}</p>)}
                            {rel.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{rel.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditRelationship(rel)}
                            title="Modifica titolarità"
                            data-testid={`edit-relationship-${rel.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRelationship(rel.id)}
                            className="text-red-600"
                            title="Rimuovi titolarità"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Link to Unit Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRelationship ? 'Modifica titolarità' : 'Collega a Unità Immobiliare'}
            </DialogTitle>
            <DialogDescription>
              {selectedPerson &&
                (editingRelationship
                  ? `Correggi la posizione di "${selectedPerson.full_name}" sull'unità`
                  : `Collega "${selectedPerson.full_name}" a un'unità immobiliare`)}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleLinkToUnit} className="space-y-4">
            <div>
              <Label htmlFor="unit_id">Unità Immobiliare *</Label>
              <Select
                value={linkFormData.unit_id}
                disabled={!!editingRelationship}
                onValueChange={(value) => setLinkFormData({ ...linkFormData, unit_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona unità..." />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {getUnitName(unit.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="relationship_type">Ruolo *</Label>
              <Select 
                value={linkFormData.relationship_type} 
                onValueChange={(value) => setLinkFormData({ ...linkFormData, relationship_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_TYPES.map((rel) => (
                    <SelectItem key={rel.value} value={rel.value}>
                      {rel.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(linkFormData.relationship_type === 'CO_OWNER' || linkFormData.relationship_type === 'OWNER') && (
              <div>
                <Label htmlFor="ownership_percentage">Quota di Proprietà (%)</Label>
                <Input 
                  id="ownership_percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={linkFormData.ownership_percentage}
                  onChange={(e) => setLinkFormData({ ...linkFormData, ownership_percentage: e.target.value })}
                  placeholder="es. 50 per il 50%"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Inserisci la quota percentuale di proprietà (es. 50 per comproprietà al 50%)
                </p>
              </div>
            )}
            
            <TenureFields value={linkFormData} onChange={setLinkFormData} />

            <div>
              <Label htmlFor="link_notes">Note / riferimento all'atto</Label>
              <Textarea 
                id="link_notes"
                value={linkFormData.notes}
                onChange={(e) => setLinkFormData({ ...linkFormData, notes: e.target.value })}
                placeholder="Note aggiuntive sul collegamento..."
                rows={2}
              />
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <input
                id="to_verify"
                type="checkbox"
                className="mt-1"
                checked={!!linkFormData.to_verify}
                onChange={(e) =>
                  setLinkFormData({ ...linkFormData, to_verify: e.target.checked })
                }
                data-testid="relationship-to-verify"
              />
              <div>
                <Label htmlFor="to_verify" className="cursor-pointer">
                  Titolarità da verificare
                </Label>
                <p className="text-xs text-muted-foreground">
                  Da spuntare quando la posizione non è ancora accertata: subentri in corso,
                  successioni aperte, unità con due nominativi in contrasto.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={loading || !linkFormData.unit_id}>
                {loading
                  ? 'Salvataggio...'
                  : editingRelationship
                  ? 'Salva modifiche'
                  : 'Collega'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documenti della persona Dialog */}
      <Dialog open={docsDialogOpen} onOpenChange={setDocsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Documenti del soggetto</DialogTitle>
            <DialogDescription>
              {docsPerson && `Allega e gestisci i documenti di "${docsPerson.full_name}" (es. documento d'identità). Formati ammessi: PDF, JPG, PNG, Word, Excel.`}
            </DialogDescription>
          </DialogHeader>

          {/* Upload */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end border-b pb-4">
            <div className="flex-1 w-full">
              <Label>Tipo documento</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERSON_DOC_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <input
                id="person-doc-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={handleUploadDoc}
                disabled={uploadingDoc}
              />
              <Button
                type="button"
                onClick={() => document.getElementById('person-doc-file').click()}
                disabled={uploadingDoc}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingDoc ? 'Caricamento...' : 'Carica file'}
              </Button>
            </div>
          </div>

          {/* Lista documenti */}
          <div className="mt-2">
            {docsLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Caricamento...</p>
            ) : personDocs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Paperclip className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                <p>Nessun documento allegato</p>
                <p className="text-sm">Carica il documento d'identità o altri allegati del soggetto</p>
              </div>
            ) : (
              <div className="space-y-2">
                {personDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{doc.title || doc.file_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="secondary">
                            {(PERSON_DOC_TYPES.find(t => t.value === doc.doc_type) || {}).label || doc.doc_type}
                          </Badge>
                          {doc.file_size ? (
                            <span className="text-xs text-muted-foreground">{(doc.file_size / 1024).toFixed(0)} KB</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadDoc(doc)} title="Scarica">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-red-600" title="Elimina">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Excel Import Dialog */}
      <ExcelImportDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        importType="people"
        condominiumId={condominiumId}
        onSuccess={() => {
          fetchPeople();
        }}
      />
    </Card>
  );
};
